import React from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { usePermissions } from '../contexts/PermissionsContext';
import {
  PERMISSION_IR_SPECTRUM,
  PERMISSION_SPECTRUM,
  PERMISSION_EXACT,
  PERMISSION_PUBLICATION_COMPOUND,
  PERMISSION_SUB,
  PERMISSION_PREDICTION_STRUCT2NMR,
  PERMISSION_PREDICTION_NMR2STRUCT,
} from '../services/Login';
import ProgressControl from '../components/ProgressControl';

import MainPage from '../pages/MainPage';
import PremiumPage from '../pages/PremiumPage';
import LoginPage from '../pages/Login/LoginPage';
import PersonalInfoPage from '../pages/Login/PersonalInfoPage';
import HelpPage from '../pages/HelpPage';
import PublicationSearchPage from '../pages/Publication/PublicationSearchPage';
import CompoundSearchPage from '../pages/Compound/CompoundSearchPage';
import SpectrumSearchPage from '../pages/Spectrum/SpectrumSearchPage';
import SpectrumSearchResultPage from '../pages/Spectrum/SpectrumSearchResultPage';
import SpectrumComparePage from '../pages/Spectrum/SpectrumComparePage';
import IRSpectrumSearchPage from '../pages/Spectrum/IRSpectrumSearchPage';
import IRSpectrumPreviewPage from '../pages/Spectrum/IRSpectrumPreviewPage';
import IRSpectrumResultPage from '../pages/Spectrum/IRSpectrumResultPage';
import IRSpectrumCompare from '../pages/Spectrum/IRSpectrumCompare';
import SubStructureSearchPage from '../pages/Compound/SubSearchPage';
import ExactSearchPage from '../pages/Compound/ExactSearchPage';
import SpectrumPage from '../pages/Spectrum/SpectrumPage';
import RegisterPage from '../pages/Login/RegisterPage';
import ConfirmationPage from '../pages/Login/ConfirmationPage';
import RestoreConfirmationPage from '../pages/Login/RestoreConfirmationPage';
import RestorePage from '../pages/Login/RestorePage';
import HistoryPage from '../pages/History/HistoryPage';
import PublicationHistoryPage from '../pages/History/PublicationHistoryPage';
import CompoundHistoryPage from '../pages/History/CompoundHistoryPage';
import PrivacyPolicyPage from '../pages/Login/PrivacyPolicyPage';
import PredictSpectrumSearch from '../pages/Predict/PredictSpectrumSearch';
import PredictSpectrumResult from '../pages/Predict/PredictSpectrumResult';
import PredictCompoundResult from '../pages/Predict/PredictCompoundResult';
import PredictCompoundSearch from '../pages/Predict/PredictCompoundSearch';
import PredictCompoundPreview from '../pages/Predict/PredictCompoundPreview';



// Protected Route component
const ProtectedRoute = ({ component: Component, permission, ...rest }) => {
  const { hasPermission, loading, isAccountExpired } = usePermissions();

  return (
    <Route
      {...rest}
      render={(props) => {
        // If still loading permissions, show loading indicator
        if (loading) {
          return <ProgressControl isLoading={loading} />;
        }

        if (isAccountExpired) {
          return (
            <Redirect
              to={{
                pathname: "/personal",
                state: { from: props.location, expired: true }
              }}
            />
          );
        }
         
        // If user has permission, render the component
        if (!permission || hasPermission(permission)) {
          return <Component {...props} />;
        }

        // Otherwise redirect to access denied page
        return (
          <Redirect
            to={{
              pathname: "/premium",
              state: { from: props.location }
            }}
          />
        );
      }}
    />
  );
};

const MainRoutes = () => {
  return (
    <Switch>
      {/* Public routes */}
      <Route exact path="/" component={MainPage} />
      <Route path="/main" component={MainPage} />
      <Route path="/login" component={LoginPage} />
      <Route path="/help" component={HelpPage} />
      <Route path="/access-denied" component={PersonalInfoPage} />
      <Route path="/personal" component={PersonalInfoPage} />

      {/* New routes */}
      <Route path="/exact-search/:molid">
        {(props: any) => <ExactSearchPage {...props} />}
      </Route>
      <Route path="/exact-search/">
        {(props: any) => <ExactSearchPage {...props} />}
      </Route>
      <Route path="/publication-search/:publicationId">
        {(props: any) => <PublicationSearchPage {...props} />}
      </Route>
      <Route path="/spectrum/:spectrid">
        {(props: any) => <SpectrumPage {...props} />}
      </Route>
      <Route exact path="/register" render={(props: any) => <RegisterPage {...props} />} />
      <Route exact path="/confirmation" render={(props: any) => <ConfirmationPage {...props} />} />
      <Route exact path="/restore-confirmation" render={(props: any) => <RestoreConfirmationPage {...props} />} />
      <Route exact path="/restore" render={(props: any) => <RestorePage {...props} />} />
      <Route exact path="/search-history" render={(props: any) => <HistoryPage {...props} />} />
      <Route exact path="/publication-history" render={(props: any) => <PublicationHistoryPage {...props} />} />
      <Route exact path="/compound-history" render={(props: any) => <CompoundHistoryPage {...props} />} />
      <Route exact path="/privacy-policy" render={(props: any) => <PrivacyPolicyPage {...props} />} />
      <Route exact path="/premium" render={(props: any) => <PremiumPage {...props} />} />
      <Route path="/compound-search">
        {(props: any) => <CompoundSearchPage {...props} />}
      </Route>


      <ProtectedRoute
        path="/predict-compound-preview"
        component={PredictCompoundPreview}
        permission={PERMISSION_PREDICTION_NMR2STRUCT}
      />
      <ProtectedRoute
        path="/predict-compound-search"
        component={PredictCompoundSearch}
        permission={PERMISSION_PREDICTION_NMR2STRUCT}
      />
      <ProtectedRoute
        path="/predict-spectrum-result"
        component={PredictSpectrumResult}
        permission={PERMISSION_PREDICTION_STRUCT2NMR}
      />
      <ProtectedRoute
        path="/predict-compound-result"
        component={PredictCompoundResult}
        permission={PERMISSION_PREDICTION_NMR2STRUCT}
      />
      
      <ProtectedRoute
        path="/sub-search/predict"
        component={SubStructureSearchPage}
        permission={PERMISSION_PUBLICATION_COMPOUND}
      />

      {/* Protected routes - specific to permissions */}
      <ProtectedRoute
        path="/publication-search"
        component={PublicationSearchPage}
        permission={PERMISSION_PUBLICATION_COMPOUND}
      />
      
      <ProtectedRoute
        path="/publication-search-result/:searchid"
        component={PublicationSearchPage}
        permission={PERMISSION_PUBLICATION_COMPOUND}
      />

      <ProtectedRoute
        path="/compound-search"
        component={CompoundSearchPage}
        permission={PERMISSION_EXACT}
      />

      <ProtectedRoute
        path="/sub-search"
        component={SubStructureSearchPage}
        permission={PERMISSION_SUB}
      />

      <ProtectedRoute
        path="/spectrum-search"
        component={SpectrumSearchPage}
        permission={PERMISSION_SPECTRUM}
      />
      <ProtectedRoute
        path="/spectrum-search-result/:searchid"
        component={SpectrumSearchResultPage}
        permission={PERMISSION_SPECTRUM}
      />

      <Route path="/spectrum-search-result">
        {(props: any) => <SpectrumSearchResultPage {...props} />}
      </Route>


      <ProtectedRoute
        path="/spectrum-compare/:spectrid"
        component={SpectrumComparePage}
        permission={PERMISSION_SPECTRUM}
      />

      <ProtectedRoute
        path="/predict-spectrum-search"
        component={PredictSpectrumSearch}
        permission={PERMISSION_PREDICTION_STRUCT2NMR}
      />


      {/* IR Spectrum Routes */}
      <ProtectedRoute
        path="/ir-spectrum-search"
        component={IRSpectrumSearchPage}
        permission={PERMISSION_IR_SPECTRUM}
      />
      <ProtectedRoute
        path="/ir-spectrum-preview"
        component={IRSpectrumPreviewPage}
        permission={PERMISSION_IR_SPECTRUM}
      />
      <ProtectedRoute
        path="/ir-spectrum-result/:searchid?"
        component={IRSpectrumResultPage}
        permission={PERMISSION_IR_SPECTRUM}
      />
      <ProtectedRoute
        path="/ir-spectrum-compare/:spectrumId"
        component={IRSpectrumCompare}
        permission={PERMISSION_IR_SPECTRUM}
      />
      <ProtectedRoute
        path="/ir-spectrum/:spectrumId"
        component={IRSpectrumCompare}
        permission={PERMISSION_IR_SPECTRUM}
      />


      {/* Catch all route for 404 Not Found */}
      {/* <Route component={NotFoundPage} /> */}
    </Switch>
  );
};

export default MainRoutes;